import { create } from "zustand";
import { toast } from "react-toastify";

/* =======================
   TYPES
======================= */
export type JoinAvailabilityPayload = {
  joinAvailabilityId: number;
};

export type AvailabilityStatusPayload = {
  availableForHiring: boolean;
};

type AvailabilityState = {
  loading: boolean;

  updateJoinAvailability: (
    data: JoinAvailabilityPayload
  ) => Promise<boolean>;

  updateAvailabilityStatus: (
    data: AvailabilityStatusPayload
  ) => Promise<boolean>;
};

/* =======================
   HELPER
======================= */
const getAccessToken = (): string | null => {
  if (typeof window === "undefined") return null;

  try {
    const raw = localStorage.getItem("auth-storage");
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return parsed?.state?.accessToken || null;
  } catch (e) {
    console.error("❌ Failed to parse auth token:", e);
    return null;
  }
};

/* =======================
   STORE
======================= */
export const useAvailabilityStore = create<AvailabilityState>((set) => ({
  loading: false,

  /* ===== JOIN AVAILABILITY ===== */
  updateJoinAvailability: async ({ joinAvailabilityId }) => {
    try {
      set({ loading: true });

      const token = getAccessToken();
      if (!token) throw new Error("No access token");

      const res = await fetch(
        "https://admin.talentlinkindo.com/api/candidate/join-availability",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            join_availability_id: joinAvailabilityId,
          }),
        }
      );

      if (!res.ok) throw new Error("Update join availability failed");

      toast.success("Join availability updated");
      return true;
    } catch (err) {
      console.error(err);
      toast.error("Failed to update join availability");
      return false;
    } finally {
      set({ loading: false });
    }
  },

  /* ===== STATUS AVAILABILITY ===== */
  updateAvailabilityStatus: async ({ availableForHiring }) => {
    try {
      set({ loading: true });

      const token = getAccessToken();
      if (!token) throw new Error("No access token");

      const res = await fetch(
        "https://admin.talentlinkindo.com/api/candidate/availability-status",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            available_for_hiring: availableForHiring,
          }),
        }
      );

      if (!res.ok) throw new Error("Update availability status failed");

      toast.success("Availability status updated");
      return true;
    } catch (err) {
      console.error(err);
      toast.error("Failed to update availability status");
      return false;
    } finally {
      set({ loading: false });
    }
  },
}));
